<script>
import Popup from "./Popup.vue";

export default {
  components: { Popup },
  props: {
    item: {
      type: Object,
      required: true,
    },
    metadata: {
      type: Array,
      required: true,
    },
    isUpdating: {
      type: Boolean,
      default: false,
    },
    titleHelp: {
      type: String,
      default: "",
    },
    protectedMetadata: {
      type: Function,
      default: () => false,
    },
  },
  emits: ["metadata-changed"],
  data() {
    return {
      newKey: null,
      newValue: null,
      newType: "STRING",
      newAllowedValues: "",
      isAddMetadataVisible: false,
      isAdmin: this.$store.state.auth.userData.isAdmin,
    };
  },
  methods: {
    cloneMetadata(metadata = []) {
      return (metadata || []).map((item) => this.normalizeMetadata(item));
    },
    normalizeMetadata({ key, value, type, allowedValues } = {}) {
      return {
        key,
        value,
        type: type || "STRING",
        allowedValues: Array.isArray(allowedValues) ? allowedValues : [],
      };
    },
    allowedValuesList() {
      return (this.newAllowedValues || "")
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean)
        .filter((value, index, values) => values.indexOf(value) === index);
    },
    metadataTypeLabel(type) {
      return this.$t(`metadata-type-${(type || "STRING").toLowerCase()}`);
    },
    metadataAdded() {
      if (!this.isAddMetadataValid()) {
        return;
      }
      const type = this.newType || "STRING";
      const newMetadata = [
        ...this.metadata,
        {
          key: this.newKey.trim(),
          value: this.newValue.trim(),
          type,
          allowedValues: type === "ENUM" ? this.allowedValuesList() : [],
        },
      ];
      this.newKey = "";
      this.newValue = "";
      this.newType = "STRING";
      this.newAllowedValues = "";
      this.isAddMetadataVisible = false;
      this.$emit("metadata-changed", newMetadata);
    },
    showAddMetadata() {
      this.newKey = "";
      this.newValue = "";
      this.newType = "STRING";
      this.newAllowedValues = "";
      this.isAddMetadataVisible = true;
    },
    cancelAddMetadata() {
      this.newKey = "";
      this.newValue = "";
      this.newType = "STRING";
      this.newAllowedValues = "";
      this.isAddMetadataVisible = false;
    },
    metadataRemoved(itemToRemove, indexToRemove) {
      if (this.isProtectedMetadata(itemToRemove)) {
        return;
      }
      const newMetadata = this.metadata.filter((m, index) => index !== indexToRemove);
      this.$emit("metadata-changed", newMetadata);
    },
    isProtectedMetadata(metadataItem) {
      return this.protectedMetadata(metadataItem);
    },
    isAddMetadataValid() {
      const key = this.newKey && this.newKey.trim();
      const value = this.newValue && this.newValue.trim();
      if (!key || !value || this.metadata.find((metadataItem) => metadataItem.key === key)) {
        return false;
      }
      if (this.newType === "NUMBER") {
        const numberValue = Number(value);
        return !Number.isNaN(numberValue) && Number.isFinite(numberValue);
      }
      if (this.newType === "BOOLEAN" && !["true", "false"].includes(value.toLowerCase())) {
        return false;
      }
      if (this.newType === "ENUM") {
        const allowedValues = this.allowedValuesList();
        return allowedValues.length > 0 && allowedValues.includes(value);
      }
      return true;
    },
    amASteward() {
      return this.item && this.item.steward && this.item.steward.includesMe;
    },
    amAnOwner() {
      return this.item && this.item.owner && this.item.owner.includesMe;
    },
  },
  computed: {
    canAdd() {
      return (
        !this.item.suspended &&
        (this.$store.state.auth.userData.isAdmin ||
          this.amASteward() ||
          this.amAnOwner())
      );
    },
    canDelete() {
      return (
        !this.item.suspended &&
        (this.$store.state.auth.userData.isAdmin ||
          this.item.steward.includesMe ||
          this.item.owner.includesMe)
      );
    },
  },
};
</script>

<template>
  <section class="metadata">
    <section>
      <h5>
        <span class="title-with-help">
          {{ $t("metadata-heading") }}
          <span
            v-if="titleHelp"
            class="help-icon"
            :title="titleHelp"
            :aria-label="titleHelp"
          >?</span>
        </span>
      </h5>
      <div class="metadata-body">
        <div class="metadata-layout">
          <p class="description">{{ $t("metadata-description-add") }}</p>
          <div class="metadata-actions">
            <button
              type="button"
              class="add-metadata-trigger"
              :title="$t('properties.metadata-action-tooltip')"
              :aria-label="$t('properties.metadata-action-tooltip')"
              @click="showAddMetadata"
              :disabled="isUpdating || !canAdd"
            >
              <m-icon icon="add" />
              {{ isUpdating ? "Updating..." : "Add..." }}
            </button>
          </div>
        </div>

        <table class="metadata-current">
          <thead>
            <tr>
              <th>{{ $t("metadata-key-heading") }}</th>
              <th>{{ $t("metadata-type-heading") }}</th>
              <th>{{ $t("metadata-value-heading") }}</th>
              <th class="metadata-action"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(metadataItem, index) in metadata"
              :key="`${metadataItem.key}-${metadataItem.value}-${index}`"
            >
              <td>{{ metadataItem.key }}</td>
              <td>{{ metadataTypeLabel(metadataItem.type) }}</td>
              <td>{{ metadataItem.value }}</td>
              <td class="metadata-action">
                <button
                  type="button"
                  class="delete-metadata"
                  @click="metadataRemoved(metadataItem, index)"
                  v-if="canDelete && !isProtectedMetadata(metadataItem)"
                  :disabled="isUpdating"
                  :aria-label="`Delete metadata ${metadataItem.key}`"
                >
                  <m-icon class="material-icons-outlined" icon="highlight_off" />
                </button>
                <m-icon
                  class="material-icons-outlined delete-metadata-inactive md-inactive"
                  :icon="isProtectedMetadata(metadataItem) ? 'lock' : 'highlight_off'"
                  :title="isProtectedMetadata(metadataItem) ? $t('metadata-secret-controlled-by-checkbox') : null"
                  :aria-label="isProtectedMetadata(metadataItem) ? $t('metadata-secret-controlled-by-checkbox') : null"
                  v-if="!canDelete || isProtectedMetadata(metadataItem)"
                />
              </td>
            </tr>
            <tr v-if="!metadata.length">
              <td colspan="4" class="empty">No metadata added.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <Popup
        v-if="isAddMetadataVisible"
        class="metadata-add-popup"
        :defaultButtons="false"
        @cancelled="cancelAddMetadata"
      >
        <template v-slot:header>{{ $t("metadata-add-heading") }}</template>
        <template v-slot:body>
          <div class="metadata-popup">
            <label class="wide">
              <span>{{ $t("new-key-placeholder") }}</span>
              <input
                id="add-key-text-field"
                v-model="newKey"
                maxlength="100"
                :disabled="!canAdd"
              />
            </label>
            <label>
              <span>{{ $t("metadata-type-heading") }}</span>
              <select
                id="add-type-select"
                v-model="newType"
                :disabled="!canAdd"
              >
                <option value="STRING">{{ $t("metadata-type-string") }}</option>
                <option value="NUMBER">{{ $t("metadata-type-number") }}</option>
                <option value="BOOLEAN">{{ $t("metadata-type-boolean") }}</option>
                <option value="ENUM">{{ $t("metadata-type-enum") }}</option>
              </select>
            </label>
            <label v-if="newType === 'ENUM'">
              <span>{{ $t("metadata-enum-values-placeholder") }}</span>
              <input
                id="add-enum-values-text-field"
                v-model="newAllowedValues"
                maxlength="1000"
                :disabled="!canAdd"
              />
            </label>
            <label :class="{ wide: newType !== 'ENUM' }">
              <span>{{ $t("new-value-placeholder") }}</span>
              <div
                v-if="newType === 'BOOLEAN'"
                id="add-value-textarea"
                class="boolean-options"
              >
                <label>
                  <input
                    type="radio"
                    value="true"
                    v-model="newValue"
                    :disabled="!canAdd"
                  />
                  <span>true</span>
                </label>
                <label>
                  <input
                    type="radio"
                    value="false"
                    v-model="newValue"
                    :disabled="!canAdd"
                  />
                  <span>false</span>
                </label>
              </div>
              <select
                v-else-if="newType === 'ENUM'"
                id="add-value-textarea"
                v-model="newValue"
                :disabled="!canAdd || !allowedValuesList().length"
              >
                <option value="" disabled>{{ $t("metadata-enum-select-placeholder") }}</option>
                <option
                  v-for="allowedValue in allowedValuesList()"
                  :key="allowedValue"
                  :value="allowedValue"
                >{{ allowedValue }}</option>
              </select>
              <input
                v-else-if="newType === 'NUMBER'"
                id="add-value-textarea"
                v-model="newValue"
                type="number"
                step="any"
                :disabled="!canAdd"
              />
              <textarea
                v-else
                id="add-value-textarea"
                v-model="newValue"
                maxlength="1000"
                :disabled="!canAdd"
                rows="4"
              ></textarea>
            </label>
          </div>
        </template>
        <template v-slot:footer>
          <div class="metadata-popup-actions">
            <button type="button" @click.stop="cancelAddMetadata">
              {{ $t("cancel") }}
            </button>
            <button
              type="button"
              class="primary"
              @click="metadataAdded"
              :disabled="isUpdating || !canAdd || !isAddMetadataValid()"
            >
              {{ isUpdating ? "Updating..." : $t("metadata-add-heading") }}
            </button>
          </div>
        </template>
      </Popup>
    </section>
  </section>
</template>
<style lang="scss" scoped>
h5 {
  font-weight: bold;
  margin: 0 0 1rem;
  padding-right: 10.5rem;
}

.title-with-help {
  align-items: center;
  display: inline-flex;
  gap: 0.35rem;
}

.help-icon {
  align-items: center;
  background: transparent;
  border: 1px solid #1275CC;
  border-radius: 50%;
  color: #1275CC;
  cursor: help;
  display: inline-flex;
  flex: 0 0 1.25rem;
  font-size: 0.75rem;
  font-weight: 700;
  height: 1.25rem;
  justify-content: center;
  line-height: 1;
  margin: 0;
  padding: 0;
  width: 1.25rem;
}

.metadata-body {
  padding-left: 1rem;
  padding-right: 10.5rem;
}

.metadata-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  gap: 1rem;
  align-items: start;
}

.metadata-actions {
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
}

.metadata-actions > button {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  width: 10.5rem;
}

@media (max-width: 900px) {
  h5,
  .metadata-body {
    padding-right: 0;
  }

  .metadata-layout {
    grid-template-columns: 1fr;
  }

  .metadata-actions {
    position: static;
    justify-self: start;
  }
}

.description {
  margin-bottom: 1.25rem;
}

.metadata-current {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;

  th,
  td {
    border-bottom: 1px solid #e5e5e5;
    padding: 0.5rem;
    text-align: left;
    vertical-align: top;
    word-break: break-word;
  }

  th {
    font-weight: bold;
  }
}

.metadata-field {
  width: 100%;
}

::v-deep .metadata-add-popup {
  .mdc-dialog__surface {
    max-height: calc(100vh - 2rem);
  }

  form {
    max-height: calc(100vh - 2rem);
    min-height: 0;
  }

  section {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }

  footer {
    flex: 0 0 auto;
  }
}

.add-metadata-trigger {
  background: transparent;
  border: 1px solid #1275cc;
  color: #1275cc;
  margin-bottom: 1rem;
}

.metadata-popup {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  min-width: 32rem;

  label {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;

    span {
      color: #333;
      font-weight: bold;
    }
  }

  .wide {
    grid-column: 1 / -1;
  }

  input,
  select,
  textarea {
    border: 1px solid #CFCFCF;
    border-radius: 4px;
    box-sizing: border-box;
    font: inherit;
    padding: 0.55rem 0.65rem;
    width: 100%;
  }

  textarea {
    min-height: 7rem;
    resize: vertical;
  }

  .boolean-options {
    display: flex;
    gap: 1rem;
    min-height: 2.5rem;
    align-items: center;

    label {
      align-items: center;
      flex-direction: row;
      gap: 0.4rem;
    }

    input {
      width: auto;
    }

    span {
      font-weight: normal;
    }
  }
}

@media (max-width: 42rem) {
  .metadata-popup {
    grid-template-columns: 1fr;
    min-width: 0;

    .wide {
      grid-column: auto;
    }
  }
}

.empty {
  color: #767676;
  padding: 1rem 0.5rem;
}

.delete-metadata {
  cursor: pointer;
  border: 0;
  background: transparent;
  color: #1275cc;
  padding: 0;
}

.delete-metadata-inactive {
  color: #e5e5e5;
}

.metadata-action {
  width: 5rem;
  text-align: right;
}

.metadata {
  display: flex;
  flex-direction: column;

  > section {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    margin: 1rem 0;
    background: white;
    border: 1px solid #C8D2DC;
    border-radius: 8px;
    box-sizing: border-box;
    padding: 1.5rem;
    position: relative;
  }

  .warning {
    color: #ef0000;
  }
}

.buttons {
  margin-top: 1rem;
}

button {
  background: transparent;
  border: 1px solid #1275cc;
  color: #1275cc;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  font-weight: bold;
  cursor: pointer;
  line-height: 1.6rem;
  text-align: center;

  &.primary {
    background: #1275cc;
    border-color: transparent;
    color: white;
  }

  &:disabled {
    cursor: default;
    opacity: 0.2;
  }

  > i {
    vertical-align: -0.35rem;
    padding-right: 0.25rem;
  }
}

.metadata-popup-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}
</style>
